// The request fee (299 SAR) — a seriousness filter, not revenue (owner,
// 2026-09-29). Charged on a managed request, and on صنّع فكرتك when the trader
// asks the Maabar team to find the factory. Deducted from the order total if
// the trader proceeds; refunded only when no verified factory reached them
// within 10 Saudi working days; never refunded on cancellation.
//
// WHETHER a request carries a fee is decided by the SERVER, at insert: the
// trg_requests_create_fee_row trigger creates its managed_request_fees row in
// 'awaiting_payment'. The client never re-derives that rule — after inserting
// a request it asks for the row (fetchRequestFee) and pays if one is waiting.
// Suppliers can never read that table (RLS: the trader's own rows, or admin).
//
// Traders read Arabic or English; a 'zh' reader gets English (the Chinese
// language is the supplier's, and suppliers never see the fee).
import { startTelrPayment } from './telrPay';

export const FEE_STATUSES = ['awaiting_payment', 'paid', 'credited', 'refund_due', 'refunded', 'waived'];

// ⚠️ MIRRORS public.request_fee_settled(uuid), which is the authority and the
// enforcement (request_is_offerable, advance_managed_stage, create_managed_offer,
// supplier matches, factory-request-email all consult it). This copy exists
// only so the console can grey a button whose RPC would refuse anyway.
// POSITIVE list of the states that ALLOW action (golden rule 12): a fee state
// invented later reads as unsettled here, the safe direction. No row = no fee.
export const FEE_SETTLED_STATUSES = ['paid', 'credited', 'refund_due', 'refunded', 'waived'];
export function isFeeSettled(fee) {
  return !fee || FEE_SETTLED_STATUSES.includes(String(fee.status || ''));
}

// Arabic-Indic digits in Arabic copy, as the owner wrote it («٢٩٩ ر.س»).
const arDigits = (n) => String(n).replace(/\d/g, (d) => '٠١٢٣٤٥٦٧٨٩'[d]);
const amountText = (amount) => {
  const n = Number(amount);
  return Number.isFinite(n) && n > 0 ? (Number.isInteger(n) ? String(n) : n.toFixed(2)) : '299';
};

// The owner's approved wording (2026-09-29), with the amount from the config
// or the fee row so a price change cannot leave the copy behind.
export function feeNoticeText(lang, amount = 299) {
  const a = amountText(amount);
  if (lang === 'ar') {
    return `رسوم تأكيد الطلب ${arDigits(a)} ر.س، تُخصم من قيمة طلبك عند التنفيذ. وإذا ما لقينا لك مصنع موثّق خلال ١٠ أيام عمل، نردها لك كاملة. الرسوم غير مستردة عند الإلغاء.`;
  }
  return `Request confirmation fee: SAR ${a}, deducted from your order total when you proceed. If we can't find you a verified factory within 10 working days, we refund it in full. Non-refundable if you cancel.`;
}

export function feeLineLabel(lang) {
  return lang === 'ar' ? 'رسوم تأكيد الطلب' : 'Request confirmation fee';
}

// The credit line in a payment summary: shown as its own line, SAR only.
export function feeCreditLabel(lang) {
  return lang === 'ar' ? 'خصم رسوم تأكيد الطلب' : 'Request fee deducted';
}

export function feeStatusText(status, lang) {
  const ar = lang === 'ar';
  switch (status) {
    case 'awaiting_payment': return ar ? 'بانتظار دفع رسوم تأكيد الطلب' : 'Awaiting the request confirmation fee';
    case 'paid':             return ar ? 'رسوم تأكيد الطلب مدفوعة' : 'Request confirmation fee paid';
    case 'credited':         return ar ? 'خُصمت الرسوم من قيمة طلبك' : 'Fee deducted from your order';
    case 'refund_due':       return ar ? 'الرسوم قيد الاسترداد' : 'Fee refund in progress';
    case 'refunded':         return ar ? 'رُدّت الرسوم كاملة' : 'Fee refunded in full';
    case 'waived':           return ar ? 'أُعفي الطلب من الرسوم' : 'Fee waived';
    default:                 return '';
  }
}

// { enabled, amount, currency } — anon may read it (the form is shown before
// sign-in). A failed call reads as disabled: the form then shows no amount.
export async function fetchFeeConfig(sb) {
  let data = null; let error = null;
  try {
    ({ data, error } = await sb.rpc('get_managed_fee_config'));
  } catch (e) {
    error = e;   // a network failure must not take the form down with it
  }
  if (error || !data) {
    console.error('[requestFee] get_managed_fee_config failed:', error?.code || '', error?.message || 'no data');
    return { enabled: false, amount: null, currency: 'SAR' };
  }
  return { enabled: data.enabled === true, amount: Number(data.amount) || null, currency: data.currency || 'SAR' };
}

// The trader's fee row for one request, or null (no fee on this request).
export async function fetchRequestFee(sb, requestId) {
  if (!requestId) return null;
  const { data, error } = await sb.from('managed_request_fees')
    .select('id, request_id, status, amount, paid_at, refund_deadline_at, credited_amount, refunded_at')
    .eq('request_id', requestId).maybeSingle();
  if (error) {
    console.error('[requestFee] fee lookup failed for', requestId, ':', error.code || '', error.message);
    return null;
  }
  return data || null;
}

// { [requestId]: feeRow } for a list of requests — one query.
export async function fetchRequestFees(sb, requestIds) {
  const ids = [...new Set((requestIds || []).filter(Boolean))];
  if (!ids.length) return {};
  const { data, error } = await sb.from('managed_request_fees')
    .select('id, request_id, status, amount, paid_at, refund_deadline_at, credited_amount, refunded_at, refund_due_at, refund_due_reason, refund_reference, waived_at, waive_reason')
    .in('request_id', ids);
  if (error) {
    console.error('[requestFee] fee list lookup failed:', error.code || '', error.message);
    return {};
  }
  return (data || []).reduce((acc, row) => { acc[row.request_id] = row; return acc; }, {});
}

// Go to Telr's hosted page for the fee. factoryId rides the return URL so the
// return page can email the factory once the fee is paid (factory-request-email
// refuses while it is unpaid).
export function payRequestFee(requestId, { factoryId } = {}) {
  return startTelrPayment(requestId, 'managed_fee', 'order', factoryId ? { factory: factoryId } : {});
}

// After inserting a request: pay now if the server attached a fee to it.
// Returns true when the browser is on its way to Telr.
export async function payFeeIfRequired(sb, requestId, opts = {}) {
  const fee = await fetchRequestFee(sb, requestId);
  if (fee?.status !== 'awaiting_payment') return false;
  await payRequestFee(requestId, opts);
  return true;
}
